import {Card} from 'antd';
import cn from 'classnames';
import React, {useMemo, useState, Key} from 'react';

import {CustomTabs} from 'components/custom-tabs';
import {URL_REQUESTS_REQUIREMENT_RATING} from 'shared/constants/urls';
import {useAppSelector} from 'store/config/hooks';
import {selectThunkStatus} from 'store/slices/loading-state-slice';
import {selectRequestStatus} from 'store/slices/request-slice/request-selectors';
import {
    loadRequestData,
    loadRequestFieldsMetadata, loadRequestStatusesCommentData,
    loadRequestStatusesData,
} from 'store/slices/request-slice/request-slice-thunks';
import {IRequestData, ERequestStatus} from 'store/slices/request-slice/request-slice-type';

import {
    BASIC_INFORMATION_ON_REQUEST_NAME,
    PROLONGATION_REQUEST,
    RESPONSE_PROLONGATION, RESPONSE_REQUEST_ADDITIONAL_PLACEMENT_NAME,
    RESPONSE_REQUEST_NAME, RESPONSE_REQUEST_REJECTION_NAME,
} from '../constants/requests.constants';
import {useDisplayFilePermission} from '../hooks';
import {RequestHeader} from './components/request-header/request-header';
import {
    ERequestFormFormKey,
    RequestFormTitles,
    ERequestHeaderStyle,
} from './request-form.constants';
import {RequestFormContext} from './request-form.context';
import s from './request-form.module.less';
import {RequestAdditionalPlacement} from './tabs/request-additional-placement';
import {RequestInformation} from './tabs/request-information';
import {ResponseRequest} from './tabs/response-request';

interface IRequestFormProps {
  entityName: string;
  requestsData?: IRequestData;
  onClose: () => void;
}

export const RequestForm = ({
    entityName, requestsData, onClose,
}: IRequestFormProps) => {
    const [selectedTabKey, setSelectedTabKey] = useState<ERequestFormFormKey | Key>(ERequestFormFormKey.REQUESTS);
    const hasPermissionDisplayFileComponent = useDisplayFilePermission(
        {requestTypeId: requestsData?.requests?.topicRequestType?.id},
    );

    const cardTitle = requestsData?.requests?.topicRequestType?.description;
    const requestType = requestsData?.requests?.requestType || requestsData?.requests?.topicRequestType?.typeCode;
    const isRequestAdditionalPlacementType = requestType === ERequestFormFormKey.REQUEST_ADDITIONAL_PLACEMENT;
    const statuses = useAppSelector(selectRequestStatus(selectedTabKey.toString()))?.statuses || [];
    const {
        isPending: isLoadingRequestData,
    } = useAppSelector(selector => selectThunkStatus(selector, loadRequestData.typePrefix));
    const {
        isPending: isLoadingRequestStatusesData,
    } = useAppSelector(selector => selectThunkStatus(selector, loadRequestStatusesData.typePrefix));
    const {
        isPending: isLoadRequestStatusesCommentData,
    } = useAppSelector(selector => selectThunkStatus(selector, loadRequestStatusesCommentData.typePrefix));
    const {
        isPending: isLoadingRequestFieldsMetadata,
    } = useAppSelector(selector => selectThunkStatus(selector, loadRequestFieldsMetadata.typePrefix));
    const shouldDisplayRating = statuses?.find(status => status === ERequestStatus.RATE_REQUEST);
    const isLoading = isLoadingRequestData || isLoadingRequestStatusesData || isLoadingRequestFieldsMetadata
        || isLoadRequestStatusesCommentData;
    const spinnerTip = (isLoadingRequestStatusesData || isLoadRequestStatusesCommentData || isLoadingRequestData)
        ? 'Сохранение...' : 'Загрузка...';

    const RequestTabs = useMemo(() => (
        [{
            key: ERequestFormFormKey.REQUESTS,
            title: RequestFormTitles[ERequestFormFormKey.REQUESTS],
            header: (shouldDisplayRating)
                ? (
                    <RequestHeader
                        customCardStyleProps={{
                            titleClassName: ERequestHeaderStyle.CUSTOM_TITLE,
                            cardClassName: ERequestHeaderStyle.CUSTOM_CARD_TITLE,
                        }}
                        text={RequestFormTitles[ERequestFormFormKey.REQUESTS]}
                        value={requestsData?.requests?.rating}
                        commentId={requestsData?.requests?.idRequestComment}
                        requestFormKey={ERequestFormFormKey.REQUESTS}
                    />
                ) : undefined,
            content: isRequestAdditionalPlacementType
                ? (
                    <RequestAdditionalPlacement
                        requestFormKey={ERequestFormFormKey.REQUESTS}
                        entityName={entityName}
                        onClose={onClose}
                        data={requestsData?.requests}
                    />
                )
                : (
                    <RequestInformation
                        requestFormKey={ERequestFormFormKey.REQUESTS}
                        requestMetaName={BASIC_INFORMATION_ON_REQUEST_NAME}
                        entityName={entityName}
                        onClose={onClose}
                        requestType={requestType}
                        enableLinkToReport={requestsData?.enabledLink}
                        topicReportInfo={requestsData?.topicReportInfo}
                        data={requestsData?.requests}
                        hasPermissionDisplayFileComponent={hasPermissionDisplayFileComponent}
                    />
                ),
        },
        {
            key: ERequestFormFormKey.REQUEST_PROLONGATION,
            title: (requestsData?.prolongationRequest
                                && RequestFormTitles[ERequestFormFormKey.REQUEST_PROLONGATION]
            ),
            content: requestsData?.prolongationRequest && (
                <ResponseRequest
                    requestFormKey={ERequestFormFormKey.REQUEST_PROLONGATION}
                    requestCommentId={requestsData?.prolongationRequest?.idRequestComment}
                    entityName={PROLONGATION_REQUEST}
                    onClose={onClose}
                    data={requestsData?.prolongationRequest}
                    hasPermissionDisplayFileComponent={hasPermissionDisplayFileComponent}
                />
            ),
            noRenderWhileOnAnotherTab: true,
        },
        {
            key: ERequestFormFormKey.RESPONSE_PROLONGATION,
            title: (requestsData?.prolongationResponse
                                && RequestFormTitles[ERequestFormFormKey.RESPONSE_PROLONGATION]) || '',
            content: <ResponseRequest
                requestFormKey={ERequestFormFormKey.RESPONSE_PROLONGATION}
                requestCommentId={requestsData?.prolongationResponse?.idRequestComment}
                entityName={RESPONSE_PROLONGATION}
                onClose={onClose}
                data={requestsData?.prolongationResponse}
                hasPermissionDisplayFileComponent={hasPermissionDisplayFileComponent}
            />,
        },
        {
            key: ERequestFormFormKey.REQUEST_RESPONSE,
            title: (requestsData?.requestResponse
                            && RequestFormTitles[ERequestFormFormKey.REQUEST_RESPONSE]),
            header: (shouldDisplayRating || requestsData?.requestResponse?.rating != null)
                ? (
                    <RequestHeader
                        customCardStyleProps={{
                            titleClassName: ERequestHeaderStyle.CUSTOM_TITLE,
                            cardClassName: ERequestHeaderStyle.CUSTOM_CARD_TITLE,
                        }}
                        text={RequestFormTitles[ERequestFormFormKey.REQUEST_RESPONSE]}
                        value={requestsData?.requestResponse?.rating}
                        commentId={requestsData?.requestResponse?.idRequestComment}
                        requestFormKey={ERequestFormFormKey.REQUEST_RESPONSE}
                        url={URL_REQUESTS_REQUIREMENT_RATING}
                    />
                ) : undefined,
            content: <ResponseRequest
                requestFormKey={ERequestFormFormKey.REQUEST_RESPONSE}
                requestCommentId={requestsData?.requestResponse?.idRequestComment}
                isRequestAdditionalPlacementType={isRequestAdditionalPlacementType}
                entityName={
                    isRequestAdditionalPlacementType
                        ? RESPONSE_REQUEST_ADDITIONAL_PLACEMENT_NAME
                        : RESPONSE_REQUEST_NAME
                }
                onClose={onClose}
                data={requestsData?.requestResponse}
                hasPermissionDisplayFileComponent={hasPermissionDisplayFileComponent}
            />,
            noRenderWhileOnAnotherTab: true,
        },
        {
            key: ERequestFormFormKey.REQUEST_REJECTION,
            title: (requestsData?.requestRejection
                                && RequestFormTitles[ERequestFormFormKey.REQUEST_REJECTION]),
            content: <ResponseRequest
                requestFormKey={ERequestFormFormKey.REQUEST_REJECTION}
                requestCommentId={requestsData?.requestRejection?.idRequestComment}
                entityName={RESPONSE_REQUEST_REJECTION_NAME}
                onClose={onClose}
                data={requestsData?.requestRejection}
                isRequestAdditionalPlacementType={isRequestAdditionalPlacementType}
                hasPermissionDisplayFileComponent={hasPermissionDisplayFileComponent}
            />,
        },
        ]
    ), [
        shouldDisplayRating,
        isRequestAdditionalPlacementType,
        requestType,
        requestsData,
        selectedTabKey,
        hasPermissionDisplayFileComponent,
    ]);

    return (
        <Card
            title={cardTitle}
            className={cn(s['request-form'])}
        >
            <RequestFormContext.Provider
                value={{
                    selectedTabKey,
                    setSelectedTabKey,
                    entityName,
                }}
            >
                <CustomTabs
                    selectedTabKey={selectedTabKey}
                    setSelectedTabKey={setSelectedTabKey}
                    contentSpinner={{
                        isSpinning: isLoading,
                        tip: spinnerTip,
                    }}
                    tabs={RequestTabs}
                />
            </RequestFormContext.Provider>
        </Card>
    );
};
